import { takeLatest, call, put, select } from 'redux-saga/effects';
import { push } from 'connected-react-router';

// Services
import { fleetApi } from 'services/apiDigitalTicket/fleet';

// Actions
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as SnackbarsActions from 'store/ducks/snackbars/actions';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';
import PATH from 'constants/basePath';

import * as FleetActions from './actions';
import { CarInterface, FleetTypes } from './types';

function* list({
  payload,
}: ReturnType<typeof FleetActions.listCarsRequest>): any {
  try {
    const { body, filtering, isCsvFull } = payload;

    if (!filtering) {
      yield put(DialogsActions.setOpenLoading({ title: 'Carregando frota' }));
    }

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(fleetApi.get, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        FleetActions.listCarsSuccess({
          carsList: response.data.map((car: CarInterface) => ({
            ...car,
            ticketer: car.ticketer ? 'SIM' : 'NÃO',
            accessible: car.accessible ? 'SIM' : 'NÃO',
            airConditioned: car.airConditioned ? 'SIM' : 'NÃO',
          })),
          carsListFull: response.data.map((car: CarInterface) => ({
            ...car,
            ticketer: car.ticketer ? 'SIM' : 'NÃO',
            accessible: car.accessible ? 'SIM' : 'NÃO',
            airConditioned: car.airConditioned ? 'SIM' : 'NÃO',
          })),
          isCsvFull,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      if (!filtering) yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('fleet - listCars - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
        status: error.response.status,
      }),
    );
    yield put(FleetActions.listCarsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* listAllCars({
  payload,
}: ReturnType<typeof FleetActions.listAllCarsRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando frota' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(fleetApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        FleetActions.listAllCarsSuccess({
          allCarsList: response.data.map((car: CarInterface) => ({
            ...car,
            ticketer: car.ticketer ? 'SIM' : 'NÃO',
            accessible: car.accessible ? 'SIM' : 'NÃO',
            airConditioned: car.airConditioned ? 'SIM' : 'NÃO',
          })),
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('fleet - listAllCars - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
        status: error.response.status,
      }),
    );
    yield put(FleetActions.listAllCarsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* get({ payload }: ReturnType<typeof FleetActions.getCarRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(fleetApi.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(FleetActions.getCarSuccess({ carData: response.data }));
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('fleet - getCar - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(FleetActions.getCarFailure());
    yield put(DialogsActions.setClose());
  }
}

function* filterMonitoring({
  payload,
}: ReturnType<typeof FleetActions.filterMonitoringCarsRequest>): any {
  try {
    const { body, filtering } = payload;

    if (!filtering) {
      yield put(DialogsActions.setOpenLoading({ title: 'Carregando frota' }));
    }

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(fleetApi.get, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        FleetActions.filterMonitoringCarsSuccess({
          filterCarsList: response.data.map((car: CarInterface) => ({
            ...car,
            ticketer: car.ticketer ? 'SIM' : 'NÃO',
            accessible: car.accessible ? 'SIM' : 'NÃO',
            airConditioned: car.airConditioned ? 'SIM' : 'NÃO',
          })),
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
    }

    if (!filtering) yield put(DialogsActions.setClose());
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('fleet - listCars - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(FleetActions.listCarsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* filter({
  payload,
  meta,
}: ReturnType<typeof FleetActions.filterCarsRequest>): any {
  try {
    const { body, filtering } = payload;

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(fleetApi.get, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        FleetActions.filterCarsSuccess({
          filterCarsList: response.data.map((car: CarInterface) => ({
            ...car,
            ticketer: car.ticketer ? 'SIM' : 'NÃO',
            accessible: car.accessible ? 'SIM' : 'NÃO',
            airConditioned: car.airConditioned ? 'SIM' : 'NÃO',
          })),
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      if (!payload.filtering) yield put(DialogsActions.setClose());

      meta.promise.resolve(response);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('fleet - filterCars - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(FleetActions.filterCarsFailure());
    meta.promise.reject(error);
  }
}

function* create({
  payload,
}: ReturnType<typeof FleetActions.createCarRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Criando carro' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(fleetApi.create, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(FleetActions.createCarSuccess({ carData: response.data }));
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Carro criado com sucesso',
        }),
      );

      if ((payload as { vehicleType: number })?.vehicleType) {
        yield put(push(PATH.VESSELS.BASE_URL));
      } else {
        yield put(push(PATH.FLEET.BASE_URL));
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('fleet - createCar - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(FleetActions.createCarFailure());
    yield put(DialogsActions.setClose());
  }
}

function* update({
  payload,
}: ReturnType<typeof FleetActions.updateCarRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(fleetApi.update, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(FleetActions.updateCarSuccess({ carData: response.data }));
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Carro alterado com sucesso',
        }),
      );
      if ((payload as { vehicleType: number })?.vehicleType) {
        yield put(push(PATH.VESSELS.BASE_URL));
      } else {
        yield put(push(PATH.FLEET.BASE_URL));
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('fleet - updateCar - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(FleetActions.updateCarFailure());
    yield put(DialogsActions.setClose());
  }
}

function* remove({
  payload,
}: ReturnType<typeof FleetActions.deleteCarRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo carro' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(fleetApi.delete, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Carro excluído com sucesso',
        }),
      );
      yield put(DialogsActions.setClose());

      if (window.location.pathname === PATH.FLEET.BASE_URL) {
        yield put(FleetActions.listCarsRequest({}));
        yield put(push(PATH.FLEET.BASE_URL));

        return;
      }
      if (window.location.pathname === PATH.VESSELS.BASE_URL) {
        yield put(
          FleetActions.filterCarsRequest({
            body: {
              vehicleType: (payload as { vehicleType: number }).vehicleType,
            },
          }),
        );
        yield put(push(PATH.VESSELS.BASE_URL));
        return;
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('fleet - remove - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(FleetActions.deleteCarFailure());
    yield put(DialogsActions.setClose());
  }
}

function* reports({
  payload,
}: ReturnType<typeof FleetActions.listCarsReportRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando...' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(fleetApi.report, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        FleetActions.listCarsReportSuccess({
          reports: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('fleet - listreportCars - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(FleetActions.listCarsReportFailure());
    yield put(DialogsActions.setClose());
  }
}

function* reportsQuantity({
  payload,
}: ReturnType<typeof FleetActions.listCarsReportQuantityRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando...' }));

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(fleetApi.reportQuantity, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        FleetActions.listCarsReportQuantitySuccess({
          reportsQuantity: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('fleet - listreportQuantityCars - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(FleetActions.listCarsReportQuantityFailure());
    yield put(DialogsActions.setClose());
  }
}

function* getCarSettings({
  payload,
}: ReturnType<typeof FleetActions.getCarSettingsRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(fleetApi.carSettingsGet, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        FleetActions.getCarSettingsSuccess({ carSettingsData: response.data }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('fleet - getCarSettings - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(FleetActions.getCarSettingsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updateCarSettings({
  payload,
}: ReturnType<typeof FleetActions.updateCarSettingsRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(fleetApi.carSettingsUpdate, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Carro alterado com sucesso',
        }),
      );
      yield put(FleetActions.getCarSettingsRequest({}));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('fleet - updateCarSettings - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(FleetActions.updateCarSettingsFailure());
    yield put(DialogsActions.setClose());
  }
}

// eslint-disable-next-line import/prefer-default-export
export const fleetSagas = [
  takeLatest(FleetTypes.LIST_CARS_REQUEST, list),
  takeLatest(FleetTypes.GET_CAR_REQUEST, get),
  takeLatest(FleetTypes.FILTER_CARS_REQUEST, filter),
  takeLatest(FleetTypes.FILTER_MONITORING_CARS_REQUEST, filterMonitoring),
  takeLatest(FleetTypes.CREATE_CAR_REQUEST, create),
  takeLatest(FleetTypes.UPDATE_CAR_REQUEST, update),
  takeLatest(FleetTypes.DELETE_CAR_REQUEST, remove),
  takeLatest(FleetTypes.LIST_CARS_REPORT_REQUEST, reports),
  takeLatest(FleetTypes.LIST_CARS_QUANTITY_REPORT_REQUEST, reportsQuantity),
  takeLatest(FleetTypes.GET_CAR_SETTINGS_REQUEST, getCarSettings),
  takeLatest(FleetTypes.UPDATE_CAR_SETTINGS_REQUEST, updateCarSettings),
  takeLatest(FleetTypes.LIST_ALL_CARS_REQUEST, listAllCars),
];
